import { PointCloudOctree } from '@pnext/three-loader';
import * as THREE from 'three';

/**
 * Camera-independent point-cloud walker for analysis tools.
 *
 * Both the stockpile volume compute and the elevation profile sampler need
 * the same scaffolding around Potree v2 octrees:
 *
 *   1. Walk `pco.pcoGeometry.root` to select nodes intersecting the region
 *      of interest, descending only as far as a target LOD.
 *   2. Bulk-load the selected leaves in parallel (with bounded concurrency).
 *   3. Iterate every point in every leaf, transform local→world, hand the
 *      world coords to a caller-supplied predicate / accumulator.
 *   4. Yield to the browser between chunks so the UI thread stays responsive
 *      during multi-million-point sweeps.
 *
 * Callers differ only in the *region* (polygon vs polyline-with-buffer) and
 * the *per-point* logic (cell aggregation vs sample collection). Those are
 * passed in via `shouldVisitNode` and `onPoint`. Everything else — node
 * loading, concurrency, progress reporting, abort handling — is shared.
 *
 * **Why not `pco.pick()`?** Earlier versions of the stockpile tool did
 * exactly that and the result was camera-coupled garbage:
 *
 *   - `pco.pick` only sees octree nodes the camera frustum currently culls
 *     in, and only at whichever LOD Potree chose for *display*. Pan the
 *     camera and all rays missed; zoom out and you sampled coarse LOD.
 *   - Re-running the same compute from a different camera angle produced
 *     a different number — not a measurement tool.
 *
 * Walking `pco.pcoGeometry.root` directly is camera-independent: we pick
 * the LOD via `targetSpacing`, not via what the renderer happens to be
 * showing.
 *
 * **Coordinate transform** — verified against the V2 loader (decoder.worker.js,
 * decoder.ts:54-56):
 *
 *   - Position attribute values are stored in node-local Float32 coords:
 *       `x_stored = worldX − (octreeGeometry.offset.x + node.boundingBox.min.x)`
 *   - PointCloudOctree's THREE.Object3D `.position` is set to
 *     `pcoGeometry.offset` (point-cloud-octree.ts:76).
 *   - So world X = x_stored + node.boundingBox.min.x + pco.position.x,
 *     equivalently `pco.matrixWorld × Translate(node.boundingBox.min) × raw`.
 *
 * Storing per-node Float32 deltas keeps decimal precision usable even at
 * UTM scales (millions of metres from the project origin); applying the
 * per-node translate at iterate-time recovers world coords without a
 * Float64 round-trip.
 *
 * **Loading semantics** — verified against the V2 loader
 * (octree-geometry-node.ts, octree-loader.ts):
 *
 *   - `OctreeGeometryNode.load()` early-returns *successfully* when the
 *     per-geometry queue (`maxNumNodesLoading=3` default) is saturated. So
 *     calling it does NOT guarantee the node is loaded. We bypass this by
 *     calling `pcoGeometry.loader.load(node)` directly — the underlying
 *     `NodeLoader.load` only short-circuits on `node.loaded || node.loading`.
 *   - Worker concurrency is enforced by our own `runWithConcurrency` cap
 *     (default 6) — `WorkerPool.getWorker` has no cap of its own, so calling
 *     loader.load 50× in parallel would spawn 50 web workers.
 *   - `OctreeGeometryNode.children` starts as `[null × 8]` and is only
 *     populated by `parseHierarchy()` after the node loads. Loading a
 *     `nodeType === 2` (hierarchy-proxy) node fetches its hierarchy chunk
 *     and populates children. So we cannot detect leaf-vs-internal until
 *     the node is loaded; the walk has to load on-demand to descend
 *     correctly.
 *   - The `loaded` flag stays `false` even when the loader catches an
 *     internal error. We check it after every load and throw a meaningful
 *     error rather than silently producing wrong results.
 */

export class PointCloudWalkAbortError extends Error {
  constructor() {
    super('Point cloud walk aborted');
    this.name = 'AbortError';
  }
}

export class PointCloudWalkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PointCloudWalkError';
  }
}

/** Phase of the work the walker is currently in. */
export type WalkPhase =
  /** Bulk-loading the selected leaf nodes that aren't already resident. */
  | 'loading'
  /** Iterating points in every loaded node and invoking `onPoint`. */
  | 'processing';

export interface WalkPointCloudOptions {
  pco: PointCloudOctree;
  /**
   * Cull predicate applied to every node during the walk. Receives the
   * node's bounding box already transformed into world space, plus the
   * raw octree node (for callers that need `node.spacing` etc).
   *
   * Returning `false` skips this node *and* its entire subtree. Cheap test —
   * runs once per visited node, before any expensive work.
   */
  shouldVisitNode: (worldBox: THREE.Box3, node: any) => boolean;
  /**
   * Per-point callback. Coordinates are world-space (project SRID, Z-up).
   * Hot loop — called millions of times for big clouds, so the callback
   * should avoid per-call allocation.
   */
  onPoint: (worldX: number, worldY: number, worldZ: number) => void;
  /**
   * Two-phase progress callback. `phase` lets the UI label "Loading data"
   * vs "Computing" / "Sampling" / etc. Throttled internally to ~30 Hz so
   * React renders aren't drowned.
   */
  onProgress?: (phase: WalkPhase, done: number, total: number) => void;
  /** Cancellation. Thrown as `PointCloudWalkAbortError` between async boundaries. */
  signal?: AbortSignal;
  /**
   * Stop descending into nodes whose octree spacing is finer than this.
   * Each octree level halves the spacing, so this is the LOD knob for
   * sampling density. Unset → walk to the actual leaves.
   */
  targetSpacing?: number;
  /**
   * How many `loader.load(node)` calls run in parallel during the bulk-load
   * phase. We bypass the V2 loader's own queue cap (which silently no-ops
   * above 3 concurrent loads) and enforce our own. Each in-flight load
   * reserves a Web Worker; 6 is a conservative balance between wall-clock
   * speed and worker memory.
   */
  maxConcurrentLoads?: number;
}

export interface WalkPointCloudResult {
  /** How many octree leaf nodes were walked + iterated. Useful telemetry. */
  nodesUsed: number;
  /**
   * Total points iterated across all selected nodes. This is what the
   * `onPoint` callback was invoked with — your accumulator can do further
   * acceptance/rejection.
   */
  pointsProcessed: number;
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));
const yieldToBrowser = () => sleep(0);

// Yield every CHUNK_POINTS points so the UI thread gets a paint chance and
// the Cancel button stays responsive even on multi-million-point clouds.
// 50 000 points ≈ 10 ms of work on a typical CPU — comfortably under one
// browser repaint frame.
const CHUNK_POINTS = 50_000;
const PROGRESS_THROTTLE_MS = 33; // ≈30 Hz, matches typical browser repaint rate

/**
 * Robustly load a single octree node. Handles three races:
 *
 *   1. Node is already loaded → no-op.
 *   2. Node is currently being loaded (e.g. by Potree's render-driven flow) →
 *      poll until that load completes, then check loaded.
 *   3. Node is unloaded → call `loader.load(node)` directly (bypassing the
 *      `OctreeGeometryNode.load()` queue cap), await, verify `node.loaded`.
 *      If loaded is false after the await, the loader's internal try/catch
 *      swallowed an error — throw a meaningful failure rather than silently
 *      iterating against `node.geometry === undefined`.
 *
 * The polling backoff is short (25 ms) — the average node load takes 50–
 * 200 ms, so a few polls cover it without burning CPU.
 */
async function loadNodeReliably(loader: any, node: any, signal: AbortSignal | undefined): Promise<void> {
  while (node.loading) {
    if (signal?.aborted) throw new PointCloudWalkAbortError();
    await sleep(25);
  }
  if (node.loaded) return;
  if (signal?.aborted) throw new PointCloudWalkAbortError();

  await loader.load(node);

  // The loader's catch clause swallows errors and leaves loaded=false. If
  // someone else started loading concurrently between our poll and our
  // load() call, our load() short-circuited on `loading=true` and returned
  // immediately — wait for that load to finish before declaring failure.
  while (node.loading) {
    if (signal?.aborted) throw new PointCloudWalkAbortError();
    await sleep(25);
  }

  if (!node.loaded) {
    throw new PointCloudWalkError(`Failed to load octree node ${node.name ?? '?'}`);
  }
}

/**
 * Run `loaders` with a maximum of `concurrency` in-flight at any time.
 * Aborts cleanly via the signal between work items.
 */
async function runWithConcurrency(
  loaders: Array<() => Promise<void>>,
  concurrency: number,
  onEach: () => void,
  signal: AbortSignal | undefined,
): Promise<void> {
  let next = 0;
  const workers: Promise<void>[] = [];
  for (let w = 0; w < concurrency; w++) {
    workers.push(
      (async () => {
        while (true) {
          if (signal?.aborted) throw new PointCloudWalkAbortError();
          const i = next++;
          if (i >= loaders.length) return;
          await loaders[i]();
          onEach();
        }
      })(),
    );
  }
  await Promise.all(workers);
}

export async function walkPointCloud(opts: WalkPointCloudOptions): Promise<WalkPointCloudResult> {
  const { pco, shouldVisitNode, onPoint, onProgress, signal } = opts;
  const targetSpacing = opts.targetSpacing;
  const maxConcurrentLoads = opts.maxConcurrentLoads ?? 6;

  pco.updateMatrixWorld(true);
  const pcoGeometry: any = (pco as any).pcoGeometry;
  if (!pcoGeometry) throw new PointCloudWalkError('Point cloud has no octree geometry');
  // V2 has `loader: NodeLoader` and `root: OctreeGeometryNode`. V1's
  // PointCloudOctreeGeometry has different shape — we only support V2 here.
  const loader = pcoGeometry.loader;
  const root = pcoGeometry.root;
  if (!loader || !root) {
    throw new PointCloudWalkError('Unsupported point-cloud format (V2 octree expected)');
  }

  const tmpBox = new THREE.Box3();
  const bboxToWorld = (bbox: THREE.Box3): THREE.Box3 =>
    tmpBox.copy(bbox).applyMatrix4(pco.matrixWorld);

  const buildLocalToWorld = (node: any) =>
    new THREE.Matrix4()
      .makeTranslation(node.boundingBox.min.x, node.boundingBox.min.y, node.boundingBox.min.z)
      .premultiply(pco.matrixWorld);

  // ── Phase 1 — Walk the geometry octree, loading hierarchy proxies on
  // demand so children populate before we decide whether to descend.
  //
  // Loading a "proxy" node fetches its hierarchy chunk and creates child
  // OctreeGeometryNodes. The proxy itself also gets its own geometry loaded
  // as a side effect — wasted bandwidth if we're going to descend past it,
  // but unavoidable given the V2 loader's coupled fetch.
  //
  // Walk loads ARE separate from leaf loads (Phase 2) because:
  //   - Walks are sequential along a branch (each proxy must load before we
  //     know its children).
  //   - Leaves are independent and bulk-parallelisable.
  // We cap walk-load concurrency by serialising along each branch but
  // descending sibling branches in parallel (Promise.all on children).

  const selectedNodes: Array<{ node: any; localToWorld: THREE.Matrix4 }> = [];

  const walkAsync = async (node: any): Promise<void> => {
    if (!node) return;
    if (signal?.aborted) throw new PointCloudWalkAbortError();

    const worldBox = bboxToWorld(node.boundingBox);
    if (!shouldVisitNode(worldBox, node)) return;

    // Spacing already at our target — sample here.
    if (targetSpacing != null && node.spacing <= targetSpacing) {
      selectedNodes.push({ node, localToWorld: buildLocalToWorld(node) });
      return;
    }

    // Want to descend. We need children populated. Children start as [null × 8]
    // and get filled by `parseHierarchy` during this node's load. So if we see
    // no children, load the node — if it's a hierarchy proxy, children will
    // appear; if it's a true leaf, they won't.
    let childrenList = (node.children as any[]).filter((c: any) => c != null);
    if (childrenList.length === 0) {
      await loadNodeReliably(loader, node, signal);
      childrenList = (node.children as any[]).filter((c: any) => c != null);
      if (childrenList.length === 0) {
        // True leaf — sample here (already loaded above).
        selectedNodes.push({ node, localToWorld: buildLocalToWorld(node) });
        return;
      }
    }

    // Descend siblings in parallel — each branch is independent.
    await Promise.all(childrenList.map((child) => walkAsync(child)));
  };

  await walkAsync(root);

  if (selectedNodes.length === 0) {
    return { nodesUsed: 0, pointsProcessed: 0 };
  }

  // ── Phase 2 — Bulk-load selected nodes that aren't already loaded. Most
  // are unloaded at this point: the walk only loaded hierarchy proxies; the
  // selected leaves themselves haven't been touched.

  const toLoad = selectedNodes.filter(({ node }) => !node.loaded);
  if (toLoad.length > 0) {
    let loadedCount = 0;
    onProgress?.('loading', 0, toLoad.length);
    await runWithConcurrency(
      toLoad.map(({ node }) => () => loadNodeReliably(loader, node, signal)),
      maxConcurrentLoads,
      () => {
        loadedCount += 1;
        onProgress?.('loading', loadedCount, toLoad.length);
      },
      signal,
    );
  }

  if (signal?.aborted) throw new PointCloudWalkAbortError();

  // ── Phase 3 — Iterate point positions in each loaded node.
  //
  // Progress reporting uses **point count** as the denominator rather than
  // node count. With a per-node denominator and a small `selectedNodes`
  // (e.g. 4 nodes for a small region) the bar advanced in 4 visible jumps
  // of 25 % each — the user perceived this as "the bar is jumping". A
  // points-based denominator gives smooth fill regardless of how many or
  // how unevenly-sized the nodes are.
  //
  // We update at most every PROGRESS_THROTTLE_MS to avoid spamming React
  // with state updates faster than it can repaint.

  // Pre-tally total point count across all nodes that have geometry. Drives
  // the smooth progress bar's denominator and lets us yield mid-node for
  // very large nodes (where one node could otherwise hog the main thread
  // for >100 ms).
  let totalPoints = 0;
  for (const { node } of selectedNodes) {
    const attr = node.geometry?.getAttribute('position') as THREE.BufferAttribute | undefined;
    if (attr) totalPoints += attr.count;
  }

  let pointsProcessed = 0;
  let lastProgressEmit = 0;
  const emitProgress = (force: boolean) => {
    const now = performance.now();
    if (force || now - lastProgressEmit > PROGRESS_THROTTLE_MS) {
      lastProgressEmit = now;
      onProgress?.('processing', pointsProcessed, totalPoints);
    }
  };

  emitProgress(true);

  const localPos = new THREE.Vector3();

  for (let n = 0; n < selectedNodes.length; n++) {
    if (signal?.aborted) throw new PointCloudWalkAbortError();
    const { node, localToWorld } = selectedNodes[n];
    const geometry: THREE.BufferGeometry | undefined = node.geometry;
    if (!geometry) {
      // loadNodeReliably would have thrown if the load failed, so reaching
      // here means a node decoded successfully but produced no geometry —
      // the worker's empty-cloud path. Skip silently.
      continue;
    }
    const positionAttr = geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
    if (!positionAttr) continue;
    const count = positionAttr.count;

    let chunkStart = 0;
    while (chunkStart < count) {
      if (signal?.aborted) throw new PointCloudWalkAbortError();
      const chunkEnd = Math.min(chunkStart + CHUNK_POINTS, count);
      for (let i = chunkStart; i < chunkEnd; i++) {
        // Read raw local coords; transform to world via the precomputed matrix.
        // (Hot loop — no per-point allocation inside this branch.)
        localPos.set(positionAttr.getX(i), positionAttr.getY(i), positionAttr.getZ(i));
        localPos.applyMatrix4(localToWorld);
        onPoint(localPos.x, localPos.y, localPos.z);
      }
      pointsProcessed += chunkEnd - chunkStart;
      chunkStart = chunkEnd;

      emitProgress(false);
      // Yield mid-node when there's more work to do (this node or the next).
      const moreWork = chunkStart < count || n < selectedNodes.length - 1;
      if (moreWork) await yieldToBrowser();
    }
  }

  // Force one final progress emit so the bar reaches 100 % even if the last
  // throttled tick fell inside the throttle window.
  emitProgress(true);

  return { nodesUsed: selectedNodes.length, pointsProcessed };
}
